import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, MessageCircle, Linkedin, Github as GithubIcon } from "lucide-react";
import { OWNER } from "@/data/portfolio";
import { useReveal } from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const { ref, visible } = useReveal();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/contact`, form);
      if (data?.status === "success") {
        toast.success("Message sent! Sunil will reply within a few hours.");
        setForm(initial);
      } else {
        toast.error("Could not send your message. Please try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappUrl = `https://wa.me/${OWNER.whatsapp}?text=${encodeURIComponent("Hi Sunil, I'd like to enquire about a website.")}`;

  return (
    <section id="contact" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10" ref={ref}>
        <div className={`max-w-2xl mb-14 fade-in-up ${visible ? "in" : ""}`}>
          <div className="section-label mb-4">Contact</div>
          <h2 className="heading-hero text-4xl md:text-5xl lg:text-6xl">
            Let's build something<br/>
            <span className="text-[#FF3B30]">amazing.</span>
          </h2>
          <p className="text-white/60 mt-4">Tell me about your project. I'll get back within a few hours.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: form */}
          <form
            onSubmit={onSubmit}
            className={`lg:col-span-7 glass-card rounded-3xl p-6 md:p-10 fade-in-up ${visible ? "in" : ""}`}
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Your name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  data-testid="contact-name-input"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF3B30] transition-colors"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  data-testid="contact-email-input"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF3B30] transition-colors"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Phone (optional)</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  data-testid="contact-phone-input"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF3B30] transition-colors"
                  placeholder="+91 9xxxxxxxxx"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Subject</label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  data-testid="contact-subject-input"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF3B30] transition-colors"
                  placeholder="Website for my homestay"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Message</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={6}
                data-testid="contact-message-input"
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF3B30] transition-colors resize-none"
                placeholder="Tell me about your project, timeline, and budget…"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : (<>Send Message <Send size={16} /></>)}
            </button>
          </form>

          {/* Right: details */}
          <div className={`lg:col-span-5 flex flex-col gap-4 fade-in-up ${visible ? "in" : ""}`} style={{ transitionDelay: '150ms' }}>
            <ContactCard icon={Mail} label="Email" value={OWNER.email} href={`mailto:${OWNER.email}`} testid="contact-info-email" />
            <ContactCard icon={Phone} label="Phone" value={OWNER.phoneDisplay} href={`tel:${OWNER.phone}`} testid="contact-info-phone" />
            <ContactCard icon={MessageCircle} label="WhatsApp" value={OWNER.phoneDisplay} href={whatsappUrl} testid="contact-info-whatsapp" accent />
            <ContactCard icon={MapPin} label="Location" value={OWNER.location} testid="contact-info-location" />

            <div className="glass-card rounded-3xl p-6 flex items-center gap-3">
              <p className="text-sm text-white/60 flex-1">Also find me on</p>
              <a href={OWNER.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="w-10 h-10 grid place-items-center rounded-xl bg-white/5 border border-white/10 hover:border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 transition-colors">
                <Linkedin size={16} />
              </a>
              <a href={OWNER.github} target="_blank" rel="noopener noreferrer" data-testid="contact-github-link" className="w-10 h-10 grid place-items-center rounded-xl bg-white/5 border border-white/10 hover:border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 transition-colors">
                <GithubIcon size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon: Icon, label, value, href, testid, accent }) {
  const inner = (
    <div className={`glass-card rounded-3xl p-6 flex items-center gap-4 ${accent ? '!border-[#FF3B30]/30' : ''}`} data-testid={testid}>
      <div className={`w-12 h-12 rounded-xl grid place-items-center ${accent ? 'bg-[#FF3B30] text-white' : 'bg-white/5 border border-white/10 text-[#FF3B30]'}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest text-white/50 mb-0.5">{label}</p>
        <p className="text-sm font-medium text-white break-all">{value}</p>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="block">
        {inner}
      </a>
    );
  }
  return inner;
}
